import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isAdmin, ROLE_LABEL, type AppRole, type AppUser } from '../boot/session';
import { listAppUsers, listDrawings, listProjects, updateAppUser } from '../data/repo';
import { loadDrawing } from '../data/drawingRepo';
import { listVersions } from '../data/versionRepo';
import type { DrawingRow, ProjectSummary } from '../data/types';
import {
  createBaseDrawing,
  deleteBaseDrawing,
  listBaseDrawings,
  updateBaseDrawing,
  type BaseDrawingRow,
} from '../data/baseDrawingRepo';
import { listBranches, type BranchRow } from '../data/branchRepo';
import { parseDxfFile } from '../cad/format/dxfAdapter';
import { useAction, useAsync } from '../hooks/useAsync';
import { useUserDirectory } from '../hooks/useUserDirectory';
import { EmptyState, ErrorState, LoadingState } from '../shell/State';
import { Modal, ModalCancel } from '../shell/Modal';
import { OneUI } from '../shell/oneUi';
import { relTime } from '../shell/bits';

const ROLES: AppRole[] = [
  'admin',
  'cad_admin',
  'designer',
  'reviewer',
  'store',
  'vendor',
  'auditor',
  'user',
];

type Tab = 'users' | 'baseDrawings';

/**
 * 설정 (작업지시서 P1-07 + 기본 도면 관리)
 *
 * 두 가지를 한 화면에 묶는다:
 *   · 사용자 관리 — StoreCAD 안에서만 통하는 권한
 *   · 도면 관리   — 매장 현황 기본 도면. 사용자가 이걸 복사해 자기 안을 만든다
 *
 * ⚠️ 가입 · 승인 · 계정 생성은 전부 ONE 플랫폼이 담당한다 (통합표준 5절).
 */
export function SettingsPage({ user }: { user: AppUser }) {
  const [tab, setTab] = useState<Tab>('users');

  if (!isAdmin(user)) {
    return (
      <div className="page">
        <div className="card">
          <EmptyState
            icon="🔒"
            title="접근 권한이 없습니다"
            desc="시스템 관리자 또는 CAD 관리자만 사용할 수 있는 화면입니다."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1 className="page__title">설정</h1>
          <p className="page__desc">사용자 권한과 매장 기본 도면을 관리합니다.</p>
        </div>
      </header>

      <div className="seg" role="tablist" aria-label="설정 탭">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'users'}
          className="seg__btn"
          onClick={() => setTab('users')}
        >
          사용자 관리
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'baseDrawings'}
          className="seg__btn"
          onClick={() => setTab('baseDrawings')}
        >
          도면 관리
        </button>
      </div>

      {tab === 'users' ? <UsersTab user={user} /> : <BaseDrawingsTab />}
    </div>
  );
}

/* -------------------------------------------------------------------------- *
 * 사용자 관리
 * -------------------------------------------------------------------------- */

function UsersTab({ user }: { user: AppUser }) {
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  const state = useAsync(() => listAppUsers(), []);
  const { run, busy, error: actionError, clearError } = useAction();

  const rows = useMemo(() => {
    const list = state.data ?? [];
    const q = query.trim().toLowerCase();
    return list.filter((u) => {
      if (!showInactive && !u.active) return false;
      if (!q) return true;
      return (
        u.email.toLowerCase().includes(q) ||
        (u.name ?? '').toLowerCase().includes(q) ||
        (u.dept ?? '').toLowerCase().includes(q)
      );
    });
  }, [state.data, query, showInactive]);

  async function patch(email: string, p: { role?: AppRole; active?: boolean }) {
    const before = state.data ?? [];
    // 낙관적 갱신
    state.setData(before.map((u) => (u.email === email ? { ...u, ...p } : u)));
    const ok = await run(() => updateAppUser(email, p));
    if (!ok) state.setData(before);
  }

  return (
    <>
      <div className="notice">
        <span aria-hidden="true">ℹ</span>
        <span>
          계정 생성 · 가입 승인은 ONE 플랫폼에서 합니다. 여기 목록은 ONE 승인자가 StoreCAD 에 처음
          접속할 때 <b>최소 권한(일반)</b>으로 자동 등록되며 채워집니다.
        </span>
      </div>

      {actionError && (
        <div className="form-error" role="alert">
          {actionError}{' '}
          <button type="button" className="section__more" onClick={clearError}>
            닫기
          </button>
        </div>
      )}

      <div className="toolbar">
        <input
          className="input input--search"
          type="search"
          placeholder="이메일 · 이름 · 부서 검색"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="사용자 검색"
        />
        <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          비활성 포함
        </label>
        <div className="toolbar__spacer" />
        <span style={{ fontSize: 12, color: 'var(--one-text-muted)' }}>{rows.length}명</span>
      </div>

      {state.loading && <LoadingState label="사용자를 불러오는 중…" />}
      {!state.loading && state.error && <ErrorState message={state.error} onRetry={state.reload} />}

      {!state.loading && !state.error && rows.length === 0 && (
        <div className="card">
          <EmptyState title="표시할 사용자가 없습니다" desc="검색 조건을 바꿔 보세요." />
        </div>
      )}

      {!state.loading && !state.error && rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>이메일</th>
                <th>이름</th>
                <th>부서</th>
                <th>권한</th>
                <th>상태</th>
                <th>등록</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const isSelf = u.email === user.email;
                return (
                  <tr key={u.email}>
                    <td className="row-link__name">
                      {u.email}
                      {isSelf && (
                        <span className="tag" style={{ marginLeft: 6 }}>
                          나
                        </span>
                      )}
                    </td>
                    <td>{u.name ?? '—'}</td>
                    <td>{u.dept ?? '—'}</td>
                    <td>
                      {/* 권한 배정은 ONE 플랫폼 > 관리자 > 유저 관리 > 시스템 권한 으로 옮겼다.
                          여러 시스템에서 따로 설정하기 힘들다는 요청이라 한 곳으로 모았다.
                          여기서는 현재 값만 보여준다(코드는 남겨 두어 되살릴 수 있다). */}
                      <select
                        className="select"
                        value={u.role}
                        disabled
                        title="권한은 ONE 플랫폼 > 관리자 > 유저 관리 에서 변경합니다"
                        aria-label={`${u.email} 권한`}
                        onChange={(e) => void patch(u.email, { role: e.target.value as AppRole })}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className={u.active ? 'btn btn--sm' : 'btn btn--sm btn--danger'}
                        disabled={busy || isSelf}
                        title={isSelf ? '자기 계정은 비활성화할 수 없습니다' : undefined}
                        onClick={() => void patch(u.email, { active: !u.active })}
                      >
                        {u.active ? '✓ 활성' : '✕ 비활성'}
                      </button>
                    </td>
                    <td>{relTime(u.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- *
 * 기본 도면 관리
 * -------------------------------------------------------------------------- */

function BaseDrawingsTab() {
  const { nameOf } = useUserDirectory();
  const [rows, setRows] = useState<BaseDrawingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addingFromDrawing, setAddingFromDrawing] = useState(false);
  const [editing, setEditing] = useState<BaseDrawingRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 관리자 화면이므로 비활성 항목도 보여준다
      setRows(await listBaseDrawings(true));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleActive(row: BaseDrawingRow) {
    setBusyId(row.id);
    try {
      await updateBaseDrawing(row.id, { active: !row.active });
      setNotice(`${row.name} 을(를) ${row.active ? '숨겼습니다' : '다시 공개했습니다'}.`);
      await load();
    } catch (e) {
      setNotice(`변경 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusyId(null);
    }
  }

  async function remove(row: BaseDrawingRow) {
    if (
      !(await OneUI.confirm('이미 이 도면으로 만들어진 도면들은 그대로 남습니다(복사본이므로).', {
        title: `"${row.name}" 을(를) 완전히 삭제할까요?`,
        okText: '삭제',
        danger: true,
      }))
    ) {
      return;
    }
    setBusyId(row.id);
    try {
      await deleteBaseDrawing(row.id, row.name);
      setNotice(`${row.name} 을(를) 삭제했습니다.`);
      await load();
    } catch (e) {
      setNotice(`삭제 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <div className="notice">
        <span aria-hidden="true">ℹ</span>
        <span>
          매장의 <b>현재 모습</b>을 등록해 두면, 설계자는 프로젝트 화면에서 그것을{' '}
          <b>복사해</b> 자기 안을 만듭니다. 복사본이므로 여기서 기본 도면을 고쳐도 이미 만들어진
          도면은 바뀌지 않습니다.
          <br />
          <b>DWG 원본</b>은 프로젝트에서 "파일에서 만들기"로 먼저 가져온 뒤, 변환 결과를 확인하고{' '}
          <b>기존 도면에서 등록</b>하세요.
        </span>
      </div>

      {notice && (
        <p className="banner" role="status" style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <span>{notice}</span>
          <button type="button" className="btn btn--sm" onClick={() => setNotice(null)} aria-label="알림 닫기">
            ✕
          </button>
        </p>
      )}

      <div className="toolbar">
        <div className="toolbar__spacer" />
        <button
          type="button"
          className="btn"
          onClick={() => setAddingFromDrawing(true)}
          title="이미 만들어 둔 도면(DWG 를 변환해 만든 것 포함)을 기본 도면으로 등록합니다"
        >
          + 기존 도면에서 등록
        </button>
        <button type="button" className="btn btn--primary" onClick={() => setAdding(true)}>
          + DXF 올려서 등록
        </button>
      </div>

      {error && <ErrorState message={error} onRetry={load} />}
      {loading && <LoadingState label="기본 도면을 불러오는 중…" />}

      {!loading && !error && rows.length === 0 && (
        <div className="card">
          <EmptyState
            icon="⬚"
            title="등록된 기본 도면이 없습니다"
            desc="매장 현황 DXF 를 올리거나, 이미 만들어 둔 도면에서 등록하면 설계자가 복사해 시작할 수 있습니다."
          />
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>이름</th>
                <th>점포명</th>
                <th>층</th>
                <th>설명</th>
                <th>등록자</th>
                <th>수정</th>
                <th>상태</th>
                <th aria-label="작업"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={r.active ? undefined : { opacity: 0.6 }}>
                  <td className="row-link__name">{r.name}</td>
                  <td className="mono">{r.storeCode ?? '—'}</td>
                  <td>{r.floorName ?? '—'}</td>
                  <td>{r.description ?? '—'}</td>
                  <td title={r.createdBy}>{nameOf(r.createdBy)}</td>
                  <td>{relTime(r.updatedAt)}</td>
                  <td>
                    <span className={r.active ? 'tag' : 'tag'}>
                      {r.active ? '✓ 공개' : '✕ 숨김'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn--sm"
                        disabled={busyId !== null}
                        onClick={() => setEditing(r)}
                      >
                        수정
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm"
                        disabled={busyId !== null}
                        onClick={() => void toggleActive(r)}
                      >
                        {busyId === r.id ? '처리 중…' : r.active ? '숨기기' : '공개'}
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        disabled={busyId !== null}
                        onClick={() => void remove(r)}
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <BaseDrawingCreateModal
          onClose={() => setAdding(false)}
          onDone={(name) => {
            setAdding(false);
            setNotice(`${name} 을(를) 등록했습니다.`);
            void load();
          }}
        />
      )}

      {addingFromDrawing && (
        <BaseDrawingFromDrawingModal
          onClose={() => setAddingFromDrawing(false)}
          onDone={(name) => {
            setAddingFromDrawing(false);
            setNotice(`${name} 을(를) 등록했습니다.`);
            void load();
          }}
        />
      )}

      {editing && (
        <BaseDrawingEditModal
          row={editing}
          onClose={() => setEditing(null)}
          onDone={(name) => {
            setEditing(null);
            setNotice(`${name} 정보를 수정했습니다.`);
            void load();
          }}
        />
      )}
    </>
  );
}

/**
 * 점포명 드롭박스 — ONE 플랫폼 지점마스터(통합표준 9절, `public.branches`)에서 가져온다.
 *
 * ★ 점포번호 순으로 보여준다(요청사항) — listBranches() 가 이미 branch_no 로 정렬해
 *   돌려주므로 여기서는 그대로 렌더링만 한다.
 * ★ 지점마스터 자체는 읽기 전용이다(통합표준 9.3절) — 여기서 새 점포를 추가하지 않는다.
 * ★ 이 필드가 들고 있는 값은 여전히 점포명 문자열 하나다(점포번호는 정렬에만 쓴다) —
 *   기존 자유 입력 데이터와 호환되도록, 지금 값이 목록에 없어도 사라지지 않게 한다.
 */
function BranchSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [branches, setBranches] = useState<BranchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    listBranches()
      .then((rows) => {
        if (alive) setBranches(rows);
      })
      .catch(() => {
        if (alive) setError(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const hasCurrentInList = branches.some((b) => b.branchName === value);

  return (
    <>
      <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{loading ? '불러오는 중…' : '— 점포 선택 —'}</option>
        {value && !hasCurrentInList && <option value={value}>{value}</option>}
        {branches.map((b) => (
          <option key={b.branchNo} value={b.branchName}>
            {b.branchName}
          </option>
        ))}
      </select>
      {error && (
        <span className="field__hint">
          지점마스터를 불러오지 못했습니다 — 이전에 입력된 값은 그대로 유지됩니다.
        </span>
      )}
    </>
  );
}

/**
 * 기본 도면 등록 — DXF 원본을 올려 본문을 만든다.
 *
 * DXF 는 브라우저에서 바로 읽히므로 여기서 한 번에 끝난다.
 * DWG 는 변환 워커를 거쳐야 하고 그 파이프라인이 프로젝트·도면에 묶여 있어
 * 여기서 직접 못 받는다 → `BaseDrawingFromDrawingModal` 쪽 주석 참고.
 */
function BaseDrawingCreateModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const [name, setName] = useState('');
  const [storeCode, setStoreCode] = useState('');
  const [floorName, setFloorName] = useState('');
  const [description, setDescription] = useState('');
  const [doc, setDoc] = useState<unknown>(null);
  const [fileLabel, setFileLabel] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const ext = file.name.toLowerCase().split('.').pop();
    if (ext !== 'dxf') {
      setError(
        'DXF 만 직접 올릴 수 있습니다. DWG 는 프로젝트 화면의 "파일에서 만들기"로 가져온 뒤, 이 창을 닫고 "기존 도면에서 등록"을 쓰세요.'
      );
      return;
    }

    setBusy('DXF 읽는 중…');
    setError(null);
    try {
      const { doc: parsed } = await parseDxfFile(file);
      setDoc(parsed);
      setFileLabel(`${file.name} — 객체 ${parsed.entities.length}개`);
      if (!name.trim()) setName(file.name.replace(/\.dxf$/i, ''));
    } catch (err) {
      setError(`읽기 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(null);
    }
  }

  async function submit() {
    if (!doc) {
      setError('DXF 파일을 먼저 올리세요.');
      return;
    }
    setBusy('등록 중…');
    setError(null);
    try {
      await createBaseDrawing({
        name,
        storeCode,
        floorName,
        description,
        document: doc,
      });
      onDone(name.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(null);
    }
  }

  return (
    <Modal
      title="기본 도면 등록"
      desc="매장 현황 DXF 를 올리면 설계자가 이것을 복사해 시작할 수 있습니다."
      onClose={onClose}
      // 읽어 둔 DXF 는 입력칸에 남지 않는다(파일 칸은 비운다) — 따로 본다
      isDirty={() =>
        doc !== null || !!name.trim() || !!storeCode.trim() || !!floorName.trim() || !!description.trim()
      }
      footer={
        <>
          <ModalCancel disabled={busy !== null}>취소</ModalCancel>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={busy !== null || !doc || !name.trim()}
          >
            {busy === '등록 중…' ? '등록 중…' : '등록'}
          </button>
        </>
      }
    >
      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept=".dxf"
        style={{ display: 'none' }}
        onChange={(e) => void onPickFile(e)}
      />

      <label className="field">
        <span className="field__label">현황 도면 파일 (DXF)</span>
        <button
          type="button"
          className="btn"
          disabled={busy !== null}
          onClick={() => fileRef.current?.click()}
        >
          {busy === 'DXF 읽는 중…' ? 'DXF 읽는 중…' : fileLabel || 'DXF 파일 고르기'}
        </button>
      </label>

      <label className="field">
        <span className="field__label">이름</span>
        <input
          className="input"
          value={name}
          placeholder="수원점 1F 현황"
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label className="field">
          <span className="field__label">점포명</span>
          <BranchSelect id="cbd-store" value={storeCode} onChange={setStoreCode} />
        </label>
        <label className="field">
          <span className="field__label">층</span>
          <input
            className="input"
            value={floorName}
            placeholder="1F"
            onChange={(e) => setFloorName(e.target.value)}
          />
        </label>
      </div>

      <label className="field">
        <span className="field__label">설명 (선택)</span>
        <textarea
          className="input"
          rows={2}
          value={description}
          placeholder="언제 실측한 기준인지 적어두면 나중에 판단하기 쉽습니다."
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
    </Modal>
  );
}

/**
 * 기존 도면에서 기본 도면 등록.
 *
 * ★ DWG 를 기본 도면으로 쓰는 정식 경로다.
 *   DWG 는 브라우저에서 못 읽어 변환 워커를 거쳐야 하는데, 그 파이프라인은
 *   `cad_source.drawing_id` 가 NOT NULL 이고 Storage 정책도 경로 첫 세그먼트를
 *   project_id 로 판정해서 — 프로젝트에 속하지 않는 기본 도면은 그대로 못 태운다.
 *   그래서 프로젝트에서 "파일에서 만들기"로 이미 변환해 둔 도면을 골라 온다.
 *
 *   부수적으로 더 안전하다: 스플라인 근사·해치 생략 같은 변환 경고를 편집기에서
 *   확인하고 고친 뒤에 전사 기준으로 승격하게 된다. 검증 안 된 자동 변환 결과가
 *   모두가 복사하는 기준 도면이 되면 그 오차가 전 매장으로 퍼진다.
 */
function BaseDrawingFromDrawingModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [projectId, setProjectId] = useState('');
  const [drawings, setDrawings] = useState<DrawingRow[]>([]);
  const [drawingId, setDrawingId] = useState('');
  const [versions, setVersions] = useState<{ id: string; label: string }[]>([]);
  const [versionId, setVersionId] = useState('');

  const [name, setName] = useState('');
  const [storeCode, setStoreCode] = useState('');
  const [floorName, setFloorName] = useState('');
  const [description, setDescription] = useState('');

  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingDrawings, setLoadingDrawings] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 프로젝트 목록
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const rows = await listProjects();
        if (alive) setProjects(rows);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoadingProjects(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // 프로젝트를 고르면 그 안의 도면
  useEffect(() => {
    if (!projectId) {
      setDrawings([]);
      setDrawingId('');
      return;
    }
    let alive = true;
    setLoadingDrawings(true);
    void (async () => {
      try {
        const rows = await listDrawings(projectId);
        if (!alive) return;
        setDrawings(rows);
        setDrawingId('');
        setVersions([]);
        setVersionId('');
        // 점포명은 프로젝트에서 물려받는다
        const p = projects.find((x) => x.id === projectId);
        if (p?.store_code) setStoreCode(p.store_code);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoadingDrawings(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [projectId, projects]);

  // 도면을 고르면 버전 목록 (기본값은 최신)
  useEffect(() => {
    if (!drawingId) {
      setVersions([]);
      setVersionId('');
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const vs = await listVersions(drawingId);
        if (!alive) return;
        const opts = vs.map((v) => {
          // 이름이 'v2' 처럼 번호와 같으면 "v2 · v2" 가 되어 지저분하다
          const label = `v${v.version_number}`;
          return {
            id: v.id,
            label: v.name && v.name !== label ? `${label} · ${v.name}` : label,
          };
        });
        setVersions(opts);
        setVersionId(opts[0]?.id ?? '');

        const d = drawings.find((x) => x.id === drawingId);
        if (d) {
          if (!name.trim()) setName(d.name);
          if (d.floor_name) setFloorName(d.floor_name);
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
    // name 은 의도적으로 의존성에서 뺀다 — 사용자가 고친 이름을 되돌리면 안 된다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingId, drawings]);

  async function submit() {
    if (!drawingId) return;
    setBusy(true);
    setError(null);
    try {
      // 본문은 여기서 읽는다 — 목록에는 없다. Storage 에 있는 큰 도면도 처리된다.
      const loaded = await loadDrawing(drawingId, versionId || undefined);
      await createBaseDrawing({
        name,
        storeCode,
        floorName,
        description,
        document: loaded.document,
      });
      onDone(name.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="기존 도면에서 기본 도면 등록"
      desc="이미 만들어 둔 도면(DWG 를 변환해 만든 것 포함)을 매장 현황 기준으로 등록합니다."
      onClose={onClose}
      // 목록·이름이 늦게 채워져 입력칸 값으로는 못 견준다 — 고른 것·적은 것으로 본다
      isDirty={() => !!projectId || !!name.trim() || !!description.trim()}
      footer={
        <>
          <ModalCancel disabled={busy}>취소</ModalCancel>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={busy || !drawingId || !name.trim()}
          >
            {busy ? '등록 중…' : '등록'}
          </button>
        </>
      }
    >
      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}

      {loadingProjects ? (
        <LoadingState label="프로젝트를 불러오는 중…" />
      ) : (
        <>
          <label className="field">
            <span className="field__label">프로젝트 *</span>
            <select
              className="select"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">— 고르세요 —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.store_code ? ` (${p.store_code})` : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">도면 *</span>
            <select
              className="select"
              value={drawingId}
              disabled={!projectId || loadingDrawings}
              onChange={(e) => setDrawingId(e.target.value)}
            >
              <option value="">
                {loadingDrawings
                  ? '불러오는 중…'
                  : !projectId
                    ? '프로젝트를 먼저 고르세요'
                    : drawings.length === 0
                      ? '이 프로젝트에는 도면이 없습니다'
                      : '— 고르세요 —'}
              </option>
              {drawings.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.floor_name ? ` · ${d.floor_name}` : ''}
                </option>
              ))}
            </select>
          </label>

          {versions.length > 0 && (
            <label className="field">
              <span className="field__label">버전</span>
              <select
                className="select"
                value={versionId}
                onChange={(e) => setVersionId(e.target.value)}
              >
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label}
                  </option>
                ))}
              </select>
              <span className="field__hint">
                기본값은 최신 버전입니다. 변환 경고를 확인·수정한 버전을 고르세요.
              </span>
            </label>
          )}

          <label className="field">
            <span className="field__label">기본 도면 이름 *</span>
            <input
              className="input"
              value={name}
              placeholder="수원점 1F 현황"
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label className="field">
              <span className="field__label">점포명</span>
              <BranchSelect id="fbd-store" value={storeCode} onChange={setStoreCode} />
            </label>
            <label className="field">
              <span className="field__label">층</span>
              <input
                className="input"
                value={floorName}
                placeholder="1F"
                onChange={(e) => setFloorName(e.target.value)}
              />
            </label>
          </div>

          <label className="field">
            <span className="field__label">설명 (선택)</span>
            <textarea
              className="input"
              rows={2}
              value={description}
              placeholder="언제 실측한 기준인지 적어두면 나중에 판단하기 쉽습니다."
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
        </>
      )}
    </Modal>
  );
}

/** 이름·점포·층·설명만 고친다. 도면 본문 교체는 새로 등록하는 쪽이 안전하다. */
function BaseDrawingEditModal({
  row,
  onClose,
  onDone,
}: {
  row: BaseDrawingRow;
  onClose: () => void;
  onDone: (name: string) => void;
}) {
  const [name, setName] = useState(row.name);
  const [storeCode, setStoreCode] = useState(row.storeCode ?? '');
  const [floorName, setFloorName] = useState(row.floorName ?? '');
  const [description, setDescription] = useState(row.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await updateBaseDrawing(row.id, { name, storeCode, floorName, description });
      onDone(name.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`기본 도면 수정 — ${row.name}`}
      desc="도면 본문을 바꾸려면 새 버전을 따로 등록하세요. 이미 복사해 간 도면들은 영향받지 않습니다."
      onClose={onClose}
      footer={
        <>
          <ModalCancel disabled={busy}>취소</ModalCancel>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={busy || !name.trim()}
          >
            {busy ? '저장 중…' : '저장'}
          </button>
        </>
      }
    >
      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}

      <label className="field">
        <span className="field__label">이름</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label className="field">
          <span className="field__label">점포명</span>
          <BranchSelect id="ebd-store" value={storeCode} onChange={setStoreCode} />
        </label>
        <label className="field">
          <span className="field__label">층</span>
          <input
            className="input"
            value={floorName}
            onChange={(e) => setFloorName(e.target.value)}
          />
        </label>
      </div>

      <label className="field">
        <span className="field__label">설명</span>
        <textarea
          className="input"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
    </Modal>
  );
}
